import axios from 'axios';
import i18n from './i18n';
import { getAuthToken, forceRefresh, clearSession } from './auth';
import { getSuperAdminClubId } from './superAdminStore';
import { getTenantId } from './tenantStore';

// Runtime config (Railway) takes priority; falls back to Vite proxy (/api) for local dev.
const cfg = (window as any).__CONFIG__ ?? {};
export const api = axios.create({
  baseURL: cfg.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '/api',
});

// Attach auth token + tenant + super-admin club context to every request
api.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = getTenantId();
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  const clubId = getSuperAdminClubId();
  if (clubId) {
    config.headers['x-club-id'] = clubId;
  }
  // UI language (bare code: en/he/fr/de) so the backend can have AI-generated
  // content (e.g. Live recommendations, scouting briefs) respond in-language.
  const lang = (i18n.language || 'en').split('-')[0];
  config.headers['X-Lang'] = lang;
  return config;
});

// On 401: try one silent refresh, retry the request once, else back to login.
// Auth endpoints are excluded — a 401 there IS the final answer. The access request is posted
// from the login page by someone with no session, so a refresh-then-redirect there would only
// reload the page under them and throw away what they typed.
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/access-request'];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = (error.config ?? {}) as typeof error.config & { _retried?: boolean };
    const url = original.url ?? '';
    const isAuthPath = AUTH_PATHS.some((p) => url === p || url.startsWith(p + '?'));
    if (error.response?.status === 401 && !original._retried && !isAuthPath) {
      original._retried = true;
      const token = await forceRefresh();
      if (token) {
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
        return api(original);
      }
      clearSession();
      window.location.replace('/login');
      return new Promise(() => {}); // navigation in progress — don't surface a rejection
    }
    return Promise.reject(error);
  },
);
